import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { motion } from 'framer-motion';
import styles from './styles.module.scss';

const ANIMATION_DURATION = 0.5;

// Plus icon component
const PlusIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M7 1V13M1 7H13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Minus icon component
const MinusIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M1 7H13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const MobileView = ({ cards = [] }) => {
    const [expandedCardId, setExpandedCardId] = useState(null);
    const itemRefs = useRef({});

    const handleItemClick = (cardId) => {
        const wasExpanded = expandedCardId === cardId;
        const newExpandedCardId = wasExpanded ? null : cardId;
        setExpandedCardId(newExpandedCardId);

        // Scroll into view when expanding
        if (!wasExpanded && itemRefs.current[cardId]) {
            setTimeout(() => {
                const element = itemRefs.current[cardId];
                if (element) {
                    const buttonElement = element.querySelector('button');
                    if (buttonElement) {
                        const buttonPosition = buttonElement.getBoundingClientRect().top;
                        const offsetPosition = buttonPosition + window.pageYOffset - 80;
                        window.scrollTo({
                            top: Math.max(0, offsetPosition),
                            behavior: 'smooth',
                        });
                    }
                }
            }, ANIMATION_DURATION * 1000 + 50);
        }
    };

    useEffect(() => {
        if (cards && cards.length > 0) {
            setExpandedCardId(cards[0]?.image?.id);
            cards.forEach((card) => {
                const imageDiv = document.getElementById(`img-box-${card?.image?.id}`);
                const image = document.getElementById(`img-${card?.image?.id}`);
                if (imageDiv && image) {
                    imageDiv.innerHTML = '';
                    imageDiv.appendChild(image.cloneNode(true));
                }
            });
        }
    }, [cards]);

    if (!cards || cards.length === 0) {
        return null;
    }

    return (
        <div className={styles.itemsList}>
            {cards.map((card) => {
                const isExpanded = expandedCardId === card?.image?.id;

                return (
                    <div
                        key={card?.image?.id}
                        ref={(el) => {
                            if (el) {
                                itemRefs.current[card?.image?.id] = el;
                            }
                        }}
                        className={cn(styles.item, {
                            [styles.expanded]: isExpanded,
                        })}
                    >
                        <button
                            type="button"
                            className={cn(styles.itemButton, { [styles.expanded]: isExpanded })}
                            onClick={() => handleItemClick(card?.image?.id)}
                        >
                            <div className={styles.itemHeader}>
                                <div className={styles.itemTitle}>{card.title}</div>
                                <span className={styles.expandIcon}>
                                    {isExpanded ? <MinusIcon /> : <PlusIcon />}
                                </span>
                            </div>
                        </button>

                        <motion.div
                            className={styles.content}
                            initial="hidden"
                            animate={isExpanded ? 'visible' : 'hidden'}
                            variants={{
                                hidden: {
                                    height: 0,
                                    opacity: 0,
                                    marginTop: 0,
                                },
                                visible: {
                                    height: 'auto',
                                    opacity: 1,
                                },
                            }}
                            transition={{
                                duration: ANIMATION_DURATION,
                                ease: [0.25, 0.1, 0.25, 1],
                            }}
                        >
                            <div className={styles.contentInner}>
                                {card.image && (
                                    <motion.div
                                        id={`img-box-${card?.image?.id}`}
                                        className={styles.imageBox}
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={isExpanded ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
                                        transition={{ delay: 0.1, duration: ANIMATION_DURATION }}
                                    />
                                )}
                                {card.description && (
                                    <motion.p
                                        className={styles.desc}
                                        initial={{ opacity: 0 }}
                                        animate={isExpanded ? { opacity: 1 } : { opacity: 0 }}
                                        transition={{ delay: 0.15, duration: ANIMATION_DURATION }}
                                        dangerouslySetInnerHTML={{ __html: card.description }}
                                    />
                                )}
                            </div>
                        </motion.div>
                    </div>
                );
            })}
        </div>
    );
};

MobileView.propTypes = {
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            title: PropTypes.string,
            description: PropTypes.string,
            image: PropTypes.shape({
                id: PropTypes.number,
                url: PropTypes.string,
                alt: PropTypes.string,
            }),
        })
    ),
};


export default MobileView;
